// SEO landing page for "Küchenmontage Hamburg" — Talkani + master carpenter partnership.
// Positioned as a premium tier above 2-Mann-Handling: delivery + water/electric
// connection + door hinge alignment + splash-back cut. Higher price, higher margin.
import { Link } from "react-router-dom";
import {
  Hammer, ArrowRight, CheckCircle2, Clock, Phone, ShieldCheck,
  Wrench, Truck, Zap, Droplets, Award, Ruler,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import { BUSINESS_PROFILE } from "../data/seo";

// What's included at each service tier — from IKEA-style flat-pack up to
// full custom fitted kitchens with granite tops.
const TIERS = [
  {
    name: "Standard",
    price: "399",
    unit: "€ ab",
    line: "IKEA / XXXL flat-pack Küche bis 4 laufende Meter",
    features: [
      "Aufbau nach Herstelleranleitung",
      "Schrankausrichtung mit Wasserwaage",
      "Türen & Schubladen justieren",
      "Verpackung entsorgen",
    ],
    testid: "kuechen-tier-standard",
  },
  {
    name: "Komfort",
    price: "699",
    unit: "€ ab",
    highlighted: true,
    line: "Küche bis 6 lfm inkl. Anschluss von Spüle & Elektrogeräten",
    features: [
      "Alles aus Standard",
      "Wasser- & Abwasseranschluss (Spüle)",
      "Elektrogeräte-Anschluss (Ceran, Backofen, Dunstabzug)",
      "Silikonfugen an Arbeitsplatte",
    ],
    testid: "kuechen-tier-komfort",
  },
  {
    name: "Premium",
    price: "1.290",
    unit: "€ ab",
    line: "Individuelle Küche mit Zuschnitt, Blenden und Sonderanpassungen",
    features: [
      "Alles aus Komfort",
      "Arbeitsplatten-Zuschnitt vor Ort (auch Stein / Kompakt)",
      "Blenden & Passleisten anpassen",
      "Griffe & Aufbewahrungssysteme montieren",
      "Endreinigung der Baustelle",
    ],
    testid: "kuechen-tier-premium",
  },
];

const STEPS = [
  { icon: Truck, t: "Lieferung", d: "Wir holen die Küche beim Händler ab oder übernehmen die Anlieferung direkt zu Ihnen." },
  { icon: Ruler, t: "Aufmaß & Vorbereitung", d: "Wände prüfen, Anschlüsse markieren, Boden schützen — bevor die erste Schraube gedreht wird." },
  { icon: Hammer, t: "Montage", d: "Meisterbetrieb mit Talkani-Logistik im Rücken. Alles verschraubt, ausgerichtet, angeschlossen." },
  { icon: Award, t: "Abnahme & Foto-Protokoll", d: "Sie prüfen jedes Schrankdach mit uns. Fotos, Rechnung, Gewährleistung — alles digital dokumentiert." },
];

const WHY = [
  { icon: ShieldCheck, t: "Meisterbetrieb im Verbund", d: "Wir arbeiten mit einem eingetragenen Tischler-Meisterbetrieb aus Hamburg. Sie erhalten eine Gewährleistungsurkunde nach VOB/B — nicht nur einen Rechnungsbeleg." },
  { icon: Wrench, t: "Ein Ansprechpartner", d: "Sie sprechen mit Talkani. Wir koordinieren Meister, Werkzeug, Anschluss, Entsorgung. Keine Terminketten, kein Ping-Pong zwischen Firmen." },
  { icon: Zap, t: "Elektro & Wasser mit Sachkundenachweis", d: "Für Backofen, Ceran-Kochfeld, Spüle und Geschirrspüler bringen wir zertifizierte Fachkräfte mit — inklusive Anschlussprotokoll für Ihre Versicherung." },
  { icon: Droplets, t: "Sauberkeit garantiert", d: "Wir arbeiten mit Bodenschutzfolie, Staubschutz und Absaugung. Nach der Montage ist Ihre Küche schlüsselfertig — keine Nachreinigung nötig." },
];

const FAQ = [
  {
    q: "Wie viele Tage dauert eine Küchenmontage?",
    a: "Standard-Küchen (bis 6 lfm) an einem Werktag. Ab 8 lfm oder mit Sondermaßen 1,5 – 2 Tage. Wir vereinbaren feste Zeitfenster, damit Sie planen können.",
  },
  {
    q: "Was passiert, wenn ein Teil fehlt oder beschädigt ist?",
    a: "Wir prüfen die Lieferung vor Beginn und dokumentieren alles mit Fotos. Fehlteile reklamieren wir für Sie beim Händler — Sie erhalten die Küche fertig montiert, sobald das Teil da ist. Kein zusätzlicher Anfahrtsaufwand.",
  },
  {
    q: "Übernehmt ihr auch die Entsorgung der alten Küche?",
    a: "Ja. Vor der Montage bauen wir die alte Küche ab und entsorgen sie fachgerecht. Kosten je nach Umfang ab €149. Elektrogeräte werden EAR-konform der Verwertung zugeführt — Sie bekommen eine Entsorgungsbescheinigung.",
  },
  {
    q: "Bieten ihr eine Gewährleistung?",
    a: "Ja. Auf die Montageleistung 2 Jahre Gewährleistung nach BGB § 634a. Auf Anschlussarbeiten (Wasser/Elektro) 5 Jahre nach VOB/B. Bei Nachbesserungsbedarf innerhalb 24 – 48 h vor Ort.",
  },
  {
    q: "Was, wenn ich die Küche schon selbst gekauft habe?",
    a: "Perfekt — wir montieren jede Marke (IKEA, XXXL, Segmüller, Nobilia, Häcker, Ballerina, Nolte, Bulthaup u.v.m.). Sagen Sie uns nur Hersteller + Küchenplan (PDF reicht), wir prüfen und geben ein Festpreis-Angebot in 24 h.",
  },
  {
    q: "Kann ich auch nur die Montage buchen — ohne Lieferung?",
    a: "Ja. Wenn die Küche bereits bei Ihnen steht, führen wir reine Montage aus. Preis reduziert sich um €149 (Lieferpauschale). Aufmaß-Check bleibt in jedem Fall enthalten.",
  },
];

export default function KuechenMontage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Küchenmontage",
    provider: {
      "@type": "LocalBusiness",
      name: BUSINESS_PROFILE.name,
      telephone: BUSINESS_PROFILE.telephone,
      email: BUSINESS_PROFILE.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS_PROFILE.street,
        addressLocality: BUSINESS_PROFILE.city,
        postalCode: BUSINESS_PROFILE.postalCode,
        addressCountry: BUSINESS_PROFILE.country,
      },
    },
    areaServed: [
      { "@type": "City", name: "Hamburg" },
      { "@type": "City", name: "Lübeck" },
      { "@type": "City", name: "Kiel" },
    ],
    offers: [
      { "@type": "Offer", name: "Standard flat-pack Kitchen", priceCurrency: "EUR", price: "399" },
      { "@type": "Offer", name: "Komfort with connections", priceCurrency: "EUR", price: "699" },
      { "@type": "Offer", name: "Premium custom kitchen", priceCurrency: "EUR", price: "1290" },
    ],
    description:
      "Küchenmontage in Hamburg und Norddeutschland — Talkani liefert, Meisterbetrieb baut auf, Fachkräfte schließen Strom und Wasser an. Inklusive Altküchen-Entsorgung und 2 Jahren Gewährleistung.",
  };

  return (
    <div data-testid="kuechen-page" lang="de">
      <SEOHead
        title="Küchenmontage Hamburg · Meisterbetrieb ab €399 — Talkani"
        description="Wir liefern Ihre Küche, ein eingetragener Tischler-Meisterbetrieb baut sie auf, Fachkräfte schließen Strom und Wasser an. Von IKEA-Küche bis Premium mit Steinarbeitsplatte. Inklusive Altküchen-Entsorgung."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* ===== HERO ===== */}
      <section className="relative bg-[#090A0B] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-15 bg-cover bg-center" style={{ backgroundImage: "url(/generated/hausreinigung.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#090A0B] via-[#090A0B]/95 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 text-[#FF5A00] mb-4">
            <Hammer size={16} />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">Küchenmontage · Meisterbetrieb · Hamburg</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.95] max-w-4xl">
            Ihre neue Küche —<br />wir liefern &amp; bauen sie schlüsselfertig auf.
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed">
            Talkani übernimmt die Lieferung, unser eingetragener Tischler-<strong className="text-white">Meisterbetrieb</strong> die Montage,
            zertifizierte Fachkräfte den Wasser- und Elektroanschluss. Von der IKEA-Küche bis zur Premium-Küche
            mit Steinarbeitsplatte — alles aus einer Hand.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/book?service=umzug&sub=K%C3%BCchenmontage"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="kuechen-book-cta"
            >
              Festpreis-Angebot in 24 h <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${BUSINESS_PROFILE.telephone.replace(/[\s-]/g, "")}`}
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="kuechen-call-cta"
            >
              <Phone size={16} /> Direkt fragen
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-300">
            <span className="flex items-center gap-1.5"><Award size={14} className="text-[#FF5A00]" /> Meisterbetrieb im Verbund</span>
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#FF5A00]" /> Standard-Küche an 1 Werktag</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#FF5A00]" /> 2 Jahre Gewährleistung</span>
          </div>
        </div>
      </section>

      {/* ===== TIERS ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Festpreis-Pakete</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-4 max-w-3xl">
            Vom IKEA-Aufbau bis zur Premium-Küche mit Steinplatte.
          </h2>
          <p className="text-neutral-600 mb-10 max-w-2xl">
            Alle Preise verstehen sich inkl. Anfahrt in Hamburg-Stadt, Werkzeug, Materialien und Entsorgung der Verpackung.
            Umland Norddeutschland: +€0,35/km.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`p-8 border transition-all ${
                  tier.highlighted
                    ? "bg-[#090A0B] text-white border-[#090A0B] shadow-2xl scale-[1.02]"
                    : "bg-neutral-50 border-neutral-200"
                }`}
                data-testid={tier.testid}
              >
                {tier.highlighted && (
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-4">
                    ★ Meistgebucht
                  </div>
                )}
                <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${tier.highlighted ? "text-neutral-400" : "text-neutral-500"}`}>
                  {tier.name}
                </div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className={`text-xs ${tier.highlighted ? "text-neutral-400" : "text-neutral-500"}`}>{tier.unit}</span>
                  <span className="text-5xl font-black tracking-tighter">{tier.price}</span>
                  <span className="text-lg font-bold">€</span>
                </div>
                <p className={`text-sm mb-6 leading-relaxed ${tier.highlighted ? "text-neutral-300" : "text-neutral-600"}`}>
                  {tier.line}
                </p>
                <ul className="space-y-2.5 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={16} className={`mt-0.5 flex-shrink-0 ${tier.highlighted ? "text-[#FF5A00]" : "text-emerald-600"}`} />
                      <span className={tier.highlighted ? "text-neutral-200" : "text-neutral-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/book?service=umzug&sub=K%C3%BCchenmontage%20${encodeURIComponent(tier.name)}`}
                  className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 font-bold uppercase tracking-wider text-xs transition-colors ${
                    tier.highlighted
                      ? "bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                      : "bg-neutral-100 hover:bg-neutral-200 text-neutral-900"
                  }`}
                  data-testid={`${tier.testid}-book`}
                >
                  {tier.name} anfragen <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="py-20 lg:py-24 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Warum Talkani + Meisterbetrieb</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-10 max-w-3xl">
            Nicht irgendein Küchenmonteur — ein registrierter Meisterbetrieb.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {WHY.map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-white border border-neutral-200 p-6 hover:border-[#FF5A00] transition-colors">
                <div className="w-10 h-10 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center mb-4">
                  <Icon size={18} />
                </div>
                <h3 className="text-lg font-black tracking-tight mb-2">{t}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STEPS ===== */}
      <section className="py-20 lg:py-24 bg-[#090A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Ablauf</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-12 max-w-3xl">
            Vom ersten Anruf bis zur fertigen Küche.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {STEPS.map(({ icon: Icon, t, d }, i) => (
              <div key={t} className="bg-white/5 border border-white/10 p-6">
                <div className="w-10 h-10 bg-[#FF5A00] text-white flex items-center justify-center mb-4">
                  <Icon size={18} />
                </div>
                <div className="text-[#FF5A00] font-black text-2xl tracking-tighter mb-2">0{i + 1}</div>
                <div className="text-base font-black tracking-tight mb-2">{t}</div>
                <p className="text-sm text-neutral-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">Häufige Fragen</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-10">
            Alles rund um Ihre Küchenmontage.
          </h2>
          <div className="space-y-3">
            {FAQ.map(({ q, a }, i) => (
              <details
                key={q}
                className="group border border-neutral-200 bg-neutral-50 open:bg-white open:border-[#FF5A00]/40"
                data-testid={`kuechen-faq-${i}`}
              >
                <summary className="p-5 cursor-pointer list-none font-bold flex items-center justify-between gap-4">
                  <span>{q}</span>
                  <span className="text-[#FF5A00] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-neutral-700 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 lg:py-24 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Hammer size={40} className="text-[#FF5A00] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
            Küche gekauft? Schicken Sie uns den Plan.
          </h2>
          <p className="text-neutral-600 mb-8 max-w-2xl mx-auto">
            Wir prüfen Küchenplan, Anschlüsse und Zugang, und schicken Ihnen in 24 h einen Festpreis inklusive
            Meisterbetrieb-Montage, Anschluss und Altküchen-Entsorgung.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/book?service=umzug&sub=K%C3%BCchenmontage"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition inline-flex items-center gap-2"
              data-testid="kuechen-footer-book"
            >
              Festpreis anfragen <ArrowRight size={16} />
            </Link>
            <Link
              to="/2-mann-handling"
              className="bg-white border border-neutral-300 hover:border-[#FF5A00] text-neutral-900 font-bold uppercase tracking-wide px-7 py-4 transition inline-flex items-center gap-2"
              data-testid="kuechen-footer-zweimann"
            >
              Nur Lieferung: 2-Mann-Handling
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
